import { useEffect, useRef, useState, useCallback } from "react";

// WebSocket hook with auto-reconnect
export const useSocket = ({ enabled, onEvent }) => {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const onEventRef = useRef(onEvent);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(payload));
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let stopped = false;

    const buildUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const httpUrl = base.startsWith("http") ? base : `https://${base}`;
      return httpUrl.replace(/^http/, "ws") + "/api/ws";
    };

    const connect = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(buildUrl());
        wsRef.current = ws;
        ws.onopen = () => { attemptsRef.current = 0; setConnected(true); };
        ws.onclose = () => {
          setConnected(false);
          if (stopped) return;
          const wait = Math.min(15000, 1000 * Math.pow(2, attemptsRef.current++));
          reconnectRef.current = setTimeout(connect, wait);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            onEventRef.current?.(data);
          } catch {}
        };
      } catch {
        setConnected(false);
        const wait = Math.min(15000, 1000 * Math.pow(2, attemptsRef.current++));
        reconnectRef.current = setTimeout(connect, wait);
      }
    };

    connect();
    return () => {
      stopped = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      const ws = wsRef.current;
      if (ws) { try { ws.close(); } catch {} }
      wsRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  return { connected, send };
};
